// src/components/StrzelniceObszar.jsx
// Podstrona katalogu dla jednego obszaru: województwa, miasta albo powiatu.
// Wszystkie trzy mają ten sam układ (wykaz + zestawienie wg dostępności),
// różnią się źródłem danych i nagłówkami - stąd jeden komponent, nie trzy.
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import styles from '../css/modules/Page.module.css';
import list from '../css/modules/Strzelnice.module.css';
import JsonLd from '../seo/JsonLd';
import { OpinieProvider } from './Opinie';
import StrzelnicaKarta from './StrzelnicaKarta';
import { OBIEKTY, REGIONY, MIASTA, POWIATY } from '../data/strzelnice';
import { STRZELNICE_COPY } from '../seo/strzelniceCopy';
import { breadcrumbJsonLd, absoluteUrl } from '../seo/siteMeta';
import { trackEvent } from '../lib/analytics';
import { zLiczba, odmien, OBIEKTY_FORMS, POZYCJE_FORMS } from '../lib/odmiana';

/* Ile obiektów pokazujemy w zestawieniu „najlepiej udokumentowanych”.
   Przy małych obszarach nie ma sensu robić rankingu z trzech pozycji. */
const RANKING_MIN = 4;
const RANKING_MAX = 10;

const buildRegion = slug => {
  const region = REGIONY.find(r => r.slug === slug);
  if (!region) return null;
  const idx = region.cities.flatMap(c => c.idx);
  return {
    kind: 'wojewodztwo',
    slug,
    title: `Strzelnice w województwie ${region.loc}`,
    heading: `Strzelnice w województwie ${region.loc} - pełna lista`,
    crumb: `Województwo ${region.name}`,
    copy: STRZELNICE_COPY.wojewodztwa[slug],
    idx,
    cities: region.cities,
    other: region.other,
    count: region.count,
    listed: region.listed,
    region,
  };
};

const buildCity = slug => {
  const city = MIASTA.find(c => c.slug === slug);
  if (!city) return null;
  return {
    kind: 'miasto',
    slug,
    title: `Strzelnice ${city.name}`,
    heading: `Strzelnice ${city.name} - adresy, telefony i godziny`,
    crumb: city.name,
    copy: STRZELNICE_COPY.miasta[slug],
    idx: city.idx,
    count: city.count,
    listed: city.count,
    parentSlug: city.wojSlug,
    parentName: city.woj,
    parentLoc: city.wojLoc,
  };
};

const buildPowiat = slug => {
  const p = POWIATY.find(c => c.slug === slug);
  if (!p) return null;
  return {
    kind: 'powiat',
    slug,
    title: `Strzelnice - ${p.name}`,
    heading: `Strzelnice - ${p.name}`,
    crumb: p.name,
    copy: STRZELNICE_COPY.powiaty[slug],
    idx: p.idx,
    count: p.count,
    listed: p.count,
    parentSlug: p.wojSlug,
    parentName: p.woj,
    parentLoc: p.wojLoc,
  };
};

const BUILDERS = {
  wojewodztwo: buildRegion,
  miasto: buildCity,
  powiat: buildPowiat,
};

const StrzelniceObszar = ({ kind }) => {
  const { slug } = useParams();
  const area = BUILDERS[kind](slug);

  if (!area) {
    return (
      <main className={styles.page}>
        <article className={styles.wrap}>
          <h1 className={styles.h1}>Nie znaleźliśmy takiego obszaru</h1>
          <p className={styles.lead}>
            Sprawdź pełny <Link to="/strzelnice">katalog strzelnic w Polsce</Link>.
          </p>
        </article>
      </main>
    );
  }

  const items = area.idx.map(i => OBIEKTY[i]);
  const ranking = [...items].sort((a, b) => b.s - a.s || a.n.localeCompare(b.n, 'pl'));
  const stats = {
    phones: items.filter(i => i.t).length,
    sites: items.filter(i => i.w || i.f).length,
    hours: items.filter(i => i.h).length,
  };
  const showRanking = items.length >= RANKING_MIN;
  const copy = area.copy || {};

  const crumbs = [
    { name: 'Strona główna', path: '/' },
    { name: 'Strzelnice w Polsce', path: '/strzelnice' },
  ];
  if (area.parentSlug) {
    crumbs.push({
      name: `Województwo ${area.parentName}`,
      path: `/strzelnice/wojewodztwo/${area.parentSlug}`,
    });
  }
  crumbs.push({ name: area.crumb, path: `/strzelnice/${kind}/${slug}` });

  return (
    <OpinieProvider>
      <JsonLd
        data={[
          breadcrumbJsonLd(crumbs),
          {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: area.title,
            numberOfItems: ranking.length,
            itemListElement: ranking.slice(0, RANKING_MAX).map((item, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              name: item.n,
            })),
          },
        ]}
      />

      <main className={styles.page}>
        <article className={styles.wrap}>
          <nav className={styles.breadcrumbs} aria-label="Ścieżka nawigacji">
            <Link to="/">Strona główna</Link> ›{' '}
            <Link to="/strzelnice">Strzelnice</Link>
            {area.parentSlug && (
              <>
                {' › '}
                <Link to={`/strzelnice/wojewodztwo/${area.parentSlug}`}>
                  woj. {area.parentName}
                </Link>
              </>
            )}{' '}
            › <span>{area.crumb}</span>
          </nav>

          <h1 className={styles.h1}>{area.heading}</h1>

          <p className={styles.lead}>
            {copy.lead || (
              <>
                Wykaz obiektów strzeleckich - <strong>{area.count}</strong>{' '}
                {odmien(area.count, POZYCJE_FORMS)}, z czego{' '}
                <strong>{area.listed}</strong> z potwierdzonym adresem lub kontaktem.
              </>
            )}
          </p>

          <p className={styles.updated}>
            Aktualizacja: lipiec 2026 · dane z OpenStreetMap (ODbL) uzupełnione informacjami ze
            stron samych strzelnic
          </p>

          <ul className={list.stats}>
            <li>
              <span className={list.statValue}>{area.count}</span>
              <span className={list.statLabel}>
                {odmien(area.count, OBIEKTY_FORMS)}{' '}
                {area.kind === 'wojewodztwo' ? 'w województwie' : 'w wykazie'}
              </span>
            </li>
            <li>
              <span className={list.statValue}>{stats.phones}</span>
              <span className={list.statLabel}>z numerem telefonu</span>
            </li>
            <li>
              <span className={list.statValue}>{stats.sites}</span>
              <span className={list.statLabel}>z własną stroną</span>
            </li>
            <li>
              <span className={list.statValue}>{stats.hours}</span>
              <span className={list.statLabel}>z godzinami otwarcia</span>
            </li>
          </ul>

          {copy.intro && (
            <section className={styles.section}>
              {copy.intro.map(paragraph => (
                <p key={paragraph.slice(0, 40)}>{paragraph}</p>
              ))}
            </section>
          )}

          {showRanking && (
            <section className={styles.section} id="zestawienie">
              <h2>
                {area.kind === 'miasto'
                  ? `Najlepiej udokumentowane strzelnice - ${area.crumb}`
                  : `Najlepiej udokumentowane strzelnice - ${area.title.toLowerCase()}`}
              </h2>
              <p className={styles.note}>
                Kolejność wynika z <strong>kompletności opublikowanych informacji i dostępności
                obiektu</strong> (telefon, własna strona, godziny otwarcia, pełny adres, strzelnica
                kryta czynna cały rok), a nie z ocen jakości - takich danych nie zbieramy i nie
                zamierzamy ich udawać. Zasady punktacji opisujemy na stronie{' '}
                <Link to="/najlepsze-strzelnice-w-polsce">rankingu ogólnopolskiego</Link>.
              </p>
              <ol className={list.cards}>
                {ranking.slice(0, RANKING_MAX).map((item, i) => (
                  <StrzelnicaKarta
                    item={item}
                    position={i + 1}
                    showPlace={area.kind !== 'miasto'}
                    showCriteria
                    key={`rank-${item.n}-${item.c}`}
                  />
                ))}
              </ol>
            </section>
          )}

          <section className={styles.section} id="wszystkie">
            <h2>
              {area.kind === 'wojewodztwo'
                ? 'Wszystkie strzelnice według miejscowości'
                : 'Wszystkie obiekty w tym obszarze'}
            </h2>

            {area.cities ? (
              area.cities.map(city => (
                <div className={list.city} key={city.slug}>
                  <h3 className={list.cityTitle}>Strzelnice {city.name}</h3>
                  <ul className={list.cards}>
                    {city.idx.map(i => (
                      <StrzelnicaKarta item={OBIEKTY[i]} key={`${city.slug}-${OBIEKTY[i].n}`} />
                    ))}
                  </ul>
                </div>
              ))
            ) : (
              <ul className={list.cards}>
                {items.map(item => (
                  <StrzelnicaKarta item={item} showPlace key={`${item.n}-${item.c}`} />
                ))}
              </ul>
            )}

            {area.other && area.other.length > 0 && (
              <details className={list.other}>
                <summary>
                  Pozostałe obiekty bez publikowanych danych kontaktowych ({area.other.length})
                </summary>
                <p className={list.otherNote}>
                  Te obiekty są w bazie map, ale nie publikują danych, które moglibyśmy potwierdzić
                  - najczęściej są to strzelnice klubowe, leśne i myśliwskie, udostępniane po
                  uzgodnieniu z zarządcą.
                </p>
                <ul className={list.otherList}>
                  {area.other.map(name => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
              </details>
            )}
          </section>

          {area.kind === 'wojewodztwo' && (
            <RegionLinks slug={slug} />
          )}

          <div className={styles.ctaBox}>
            <h2>Teoria decyduje o zdanym egzaminie</h2>
            <p>
              Strzelnicę wybierzesz z tej listy. Część teoretyczną egzaminu na patent strzelecki
              zdasz wtedy, gdy przećwiczysz prawdziwe pytania - sprawdź się bez rejestracji.
            </p>
            <div className={styles.ctaRow}>
              <Link
                to="/intro"
                className={styles.primaryButton}
                onClick={() => trackEvent('cta_click', { location: `strzelnice_${kind}`, label: 'demo' })}
              >
                Rozwiąż darmowy test
              </Link>
              <Link to="/patent-strzelecki" className={styles.secondaryButton}>
                Patent strzelecki - przewodnik
              </Link>
            </div>
          </div>

          <section className={styles.section}>
            <h2>Zobacz też</h2>
            <ul className={styles.linkList}>
              <li>
                <Link to="/strzelnice">Katalog strzelnic w całej Polsce</Link>
              </li>
              <li>
                <Link to="/najlepsze-strzelnice-w-polsce">
                  Najlepiej udokumentowane strzelnice w Polsce - ranking
                </Link>
              </li>
              {area.parentSlug && (
                <li>
                  <Link to={`/strzelnice/wojewodztwo/${area.parentSlug}`}>
                    Wszystkie strzelnice w województwie {area.parentLoc}
                  </Link>
                </li>
              )}
              <li>
                <Link to="/test-bezpieczenstwo-strzelnica-egzamin-na-bron">
                  Regulamin strzelnicy i zasady bezpieczeństwa - pytania egzaminacyjne
                </Link>
              </li>
            </ul>
          </section>

          <p className={styles.note}>
            Dane pochodzą z OpenStreetMap (© współtwórcy OpenStreetMap, licencja ODbL) oraz ze stron
            internetowych samych strzelnic. Godziny, ceny i zasady wstępu potwierdzaj w obiekcie -
            to nie jest urzędowy rejestr. Brakuje strzelnicy albo dane są nieaktualne?{' '}
            <Link to="/kontakt">Napisz do nas</Link>.
          </p>
        </article>
      </main>
    </OpinieProvider>
  );
};

/* Linki do miast i powiatów leżących w danym województwie - bez nich podstrony
   miejskie byłyby osierocone i robot musiałby je znaleźć wyłącznie z sitemapy. */
const RegionLinks = ({ slug }) => {
  const cities = MIASTA.filter(c => c.wojSlug === slug);
  const powiaty = POWIATY.filter(p => p.wojSlug === slug);
  if (!cities.length && !powiaty.length) return null;
  return (
    <section className={styles.section}>
      <h2>Strzelnice w tym województwie według miast i powiatów</h2>
      <ul className={styles.linkList}>
        {cities.map(c => (
          <li key={c.slug}>
            <Link to={`/strzelnice/miasto/${c.slug}`}>
              Strzelnice {c.name} ({c.count})
            </Link>
          </li>
        ))}
        {powiaty.map(p => (
          <li key={p.slug}>
            <Link to={`/strzelnice/powiat/${p.slug}`}>
              Strzelnice - {p.name} ({p.count})
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default StrzelniceObszar;
